// @flow
import { get, post } from '../utils/api';

type NaurEvent = {
  id: string,
  householdId: string,
  unitId: string,
  buildingId: string,
  propertyId: string,
  organizationId: string,
  status: | 'OPEN'
    | 'PENDING_APPLICANT'
    | 'PENDING_CERTIFICATION'
    | 'PENDING_MOVE_IN'
    | 'SATISFIED',
  triggerReason: string,
  certificationType: string,
  certificationCompletedAt: string,
  hhAnnualIncome: string,
  incomeLimit: string,
  resolvedAt: ?string,
  resolvedById: ?string,
  resolutionMethod: ?string,
  resolutionNotes: ?string,
  createdAt: string,
  updatedAt: string,
  triggeringCertificationId: string,
  triggeringUnitDetails?: ?TriggeringUnitDetails,
  satisfyingUnitId?: ?string,
};

type NaurEventDetails = {
  id: string,
  householdId: string,
  unitId: string,
  buildingId: string,
  propertyId: string,
  organizationId: string,
  status: | 'OPEN'
    | 'PENDING_APPLICANT'
    | 'PENDING_CERTIFICATION'
    | 'PENDING_MOVE_IN'
    | 'SATISFIED',
  triggerReason: string,
  certificationType: string,
  certificationCompletedAt: string,
  hhAnnualIncome: number,
  incomeLimit: number,
  resolvedAt: ?string,
  resolvedById: ?string,
  resolutionMethod: ?string,
  resolutionNotes: ?string,
  triggeringCertificationId: string,
  satisfyingUnitId: ?string,
  satisfyingHouseholdId: ?string,
  satisfiedCertificationId: ?string,
  satisfiedAt: ?string,
  createdById: string,
  updatedById: string,
  createdAt: string,
  updatedAt: string,
  deletedAt: ?string,
  triggeringUnit: {
    id: string,
    number: string,
    floorPlan: {
      id: string,
      internalName: string,
      nBeds: number,
      nBaths: number,
      grossSquareFeet: number,
    },
  },
  satisfyingUnit: ?{
    id: string,
    number: string,
    unitStatus: {
      id: string,
      description: string,
    },
    floorPlan: {
      id: string,
      internalName: string,
      nBeds: number,
      nBaths: number,
      grossSquareFeet: number,
    },
    currentApplicationHousehold: ?{
      id: string,
      currentApplicationId: string,
      mainCus: {
        id: string,
        firstName: string,
        middleName: string,
        lastName: string,
      },
    },
    hu: ?{
      id: string,
      moveOutDate: string,
    },
  },
  household: {
    id: string,
    currentApplicationId: string,
    mainCus: {
      id: string,
      firstName: string,
      middleName: string,
      lastName: string,

      resident: {
        id: string,
        residentId: string,
      },
    },
    aq: ?{
      id: string,
      certificationCompletionDate: string,
      hhAnnualIncome: number,
      setAsideProgramId: ?string,
      certificationType: string,
      setAsideProgram: ?{
        id: string,
        setAsideName: string,
      },
      qualificationTestResults: ?{
        assetCalculations: {
          imputedTotal: number,
          imputedPercentage: number,
          totalHouseholdAssets: number,
          actualIncomeFromAssets: number,
          assetCashValueIsOverThreshold: boolean,
        },
        [programId: string]: | {
              result: 'Pass' | 'Fail',
              status: 'Pass' | 'Fail',
              resultBool: boolean,
              incomeLimit: number,
              propertyProgram: string,
              propertyProgramId: string,
              affordableProgramName: string,
            }
          | {
              // assetCalculations is not a program, so allow for that shape
              imputedTotal: number,
              imputedPercentage: number,
              totalHouseholdAssets: number,
              actualIncomeFromAssets: number,
              assetCashValueIsOverThreshold: boolean,
            },
      },
    },
  },
  building: {
    id: string,
    name: string,
    buildingIDNumber: string,
  },
  // UnitFloorPlanSchedule
  ufps: {
    id: string,
    status: string,
    unit: {
      id: string,
      currentResidentHousehold: ?{
        ntv: ?{
          id: string,
          moveOutDate: string,
        },
      },
    },
  },
  satisfyingCertification: ?{
    id: string,
    hhAnnualIncome: number,
    applicationId: string,
    certificationType: string,
    headOfHousehold: {
      id: string,
      firstName: string,
      middleName: string,
      lastName: string,
      resident: {
        id: string,
        residentId: string,
      },
    },
  },
};

type TriggeringUnitDetails = {
  unitNumber?: string,
  bedroomCount?: number,
  applicantName?: ?string,
};

/**
 * NAUR Events for Unit Response Type
 */
type NaurEventsResponse = {
  naurEvents: Array<NaurEvent>,
};

/**
 * NAUR Events for Building Response Type
 */
type BuildingNaurEventsResponse = {
  naurEvents: Array<NaurEvent>,
};

type BuildingNaurEventDetailsResponse = {
  naurEvents: Array<NaurEventDetails>,
};

/**
 * Unit NAUR Warning Response Type
 */
type UnitNaurWarning = {
  isImpactedByNaur: boolean,
  reason: | 'UNIT_IMPACTED_BY_NAUR'
    | 'BEDROOM_SIZE_EXCEEDS_NAUR'
    | 'NO_OPEN_NAUR_EVENTS'
    | 'UNIT_DATA_INCOMPLETE',
  unit?: {
    id: string,
    number: string,
    bedroomCount: number,
  },
  building?: {
    id: string,
    name: string,
  },
  impactingNaurEvents?: Array<{
    id: string,
    unitId: string,
    unitNumber: string,
    bedroomCount: number,
    status: string,
    householdId: string,
    certificationType: string,
    certificationCompletedAt: string,
  }>,
};

/**
 * Unit Pending Move-In NAUR Warning Response Type
 */
type UnitPendingMoveInWarning = {
  isImpactedByPendingMoveIn: boolean,
  reason: 'NO_PENDING_MOVE_IN_NAUR_EVENTS' | 'PENDING_MOVE_IN_NAUR_EXISTS',
  scheduledMoveInRestrictionDate: ?string,
  unit: {
    id: string,
    number: string,
  },
  building: {
    id: string,
    name: string,
  },
  pendingMoveInNaurEvents: Array<{
    id: string,
    unitId: string,
    satisfyingUnitId: string,
    satisfyingApplicationId: string,
    status: string,
    scheduledMoveInDate: string,
  }>,
};

type BuildingCompliance = {
  id: string,
  buildingId: string,
  isCompliant: boolean,
  statusChangedAt: string,
  currentFraction: number,
  createdAt: string,
  updatedAt: string,
};

type BuildingsWithComplianceDetails = {
  id: string,
  name: string,
  buildingIDNumber: string,
  applicableFractionUnit: number,
  applicableFractionFloorSpace: number,
  residentialUnitsCount: number,
  nonCompliantUnitsCount: number,
  buildingCompliance?: BuildingCompliance,
};

export type NaurFindingSeverity = 'BLOCKER' | 'INFO';

export type NaurFinding = {
  severity: NaurFindingSeverity,
  code: string,
  message: string,
  buildingId: ?string,
  buildingName?: ?string,
  unitId: ?string,
  unitNumber?: ?string,
  householdId: ?string,
};

export type ApplicableFractionInitializationStatus = {
  propertyId: string,
  alreadyInitialized: boolean,
  initializedAt: ?string,
  initializedById: ?string,
};

export type ApplicableFractionInitializationPreflight = {
  ...ApplicableFractionInitializationStatus,
  canInitialize: boolean,
  findings: NaurFinding[],
  buildings: Array<{
    buildingId: string,
    buildingName: string,
    canInitialize: boolean,
    residentialUnitCount: number,
    lihtcUnitCount: number,
    blockerCount: number,
  }>,
};

export type ApplicableFractionInitializationSuccess = {
  propertyId: string,
  initializedAt: string,
  initializedById: string,
  buildings: Array<{
    buildingId: string,
    buildingName: string,
    isCompliant: boolean,
    currentFractionPercentage: number,
    requiredFractionPercentage: number,
    createdNaurEventIds: string[],
  }>,
  findings: NaurFinding[],
};

/**
 * Service for NAUR (Next Available Unit Rule) Compliance operations
 */
export default class NaurService {
  /**
   * Check NAUR compliance status for a property
   * Returns whether the property has any non-compliant buildings
   *
   * @param {string} propertyId - The property ID to check
   * @param {Object} options - Optional request options (e.g., signal for abort)
   * @returns {Promise<Object>} - { hasNonCompliantBuildings: boolean }
   */
  getPropertyCompliance(propertyId: string, options?: Object): Promise<Object> {
    if (!propertyId) {
      throw new Error('Property ID is required to check NAUR compliance');
    }

    return get(`/naur/compliance/property/${propertyId}`, options);
  }

  getPropertyComplianceDetails(
    propertyId: string,
    options?: Object,
  ): Promise<{ buildings: BuildingsWithComplianceDetails[] }> {
    if (!propertyId) {
      throw new Error(
        'Property ID is required to check NAUR compliance details',
      );
    }

    return get(`/naur/compliance/property/${propertyId}/details`, options);
  }

  /**
   * Get all NAUR events for a specific unit (regardless of status)
   *
   * @param {string} unitId - The unit ID to get NAUR events for
   * @param {Object} options - Optional request options (e.g., signal for abort)
   * @returns {Promise<NaurEventsResponse>} - Array of NAUR events for the unit
   */
  getNaurEventsForUnit(
    unitId: string,
    options?: Object,
  ): Promise<NaurEventsResponse> {
    if (!unitId) {
      throw new Error('Unit ID is required to fetch NAUR events');
    }
    return get(`/naur/events/unit/${unitId}`, options);
  }

  /**
   * Check if a unit is impacted by open NAUR events
   * Returns whether the unit is impacted and related information
   *
   * @param {string} unitId - The unit ID to check
   * @param {Object} options - Optional request options (e.g., signal for abort)
   * @returns {Promise<UnitNaurWarning>} - Impact status and details
   */
  checkUnitImpact(unitId: string, options?: Object): Promise<UnitNaurWarning> {
    if (!unitId) {
      throw new Error('Unit ID is required to check NAUR impact');
    }

    return get(`/naur/warnings/unit/${unitId}`, options);
  }

  /**
   * Get all NAUR events for a specific building
   * Returns NAUR events with unit details including bedroom count and applicant names
   *
   * @param {string} buildingId - The building ID to get NAUR events for
   * @param {Object} options - Optional request options (e.g., signal for abort)
   * @returns {Promise<BuildingNaurEventsResponse>} - Response object containing NAUR events for the building
   */
  getNaurEventsForBuilding(
    buildingId: string,
    options?: Object,
  ): Promise<BuildingNaurEventsResponse> {
    if (!buildingId) {
      throw new Error('Building ID is required to fetch NAUR events');
    }

    return get(`/naur/events/building/${buildingId}`, options);
  }

  getNaurEventsDetailsForBuilding(
    buildingId: string,
    options?: Object,
  ): Promise<BuildingNaurEventDetailsResponse> {
    if (!buildingId) {
      throw new Error('Building ID is required to fetch NAUR events details');
    }

    return get(`/naur/events/building/${buildingId}/details`, options);
  }

  /**
   * Check if a unit is subject to a move-in date restriction due to a
   * PENDING_MOVE_IN NAUR event in its building.
   *
   * @param {string} unitId - The unit ID to check
   * @param {Object} options - Optional request options (e.g., signal for abort)
   * @returns {Promise<UnitPendingMoveInWarning>} - Restriction status and details
   */
  checkUnitPendingMoveInImpact(
    unitId: string,
    options?: Object,
  ): Promise<UnitPendingMoveInWarning> {
    if (!unitId) {
      throw new Error(
        'Unit ID is required to check pending move-in NAUR impact',
      );
    }

    return get(`/naur/warnings/unit/${unitId}/pending-move-in`, options);
  }

  /**
   * Read-only preflight for the one-time applicable fraction initialization.
   * Always 200; blockers and context arrive as findings.
   */
  getApplicableFractionInitialization(
    organizationId: string,
    propertyId: string,
    options?: Object,
  ): Promise<ApplicableFractionInitializationPreflight> {
    if (!organizationId || !propertyId) {
      throw new Error(
        'Organization ID and Property ID are required to check applicable fraction initialization',
      );
    }

    return get(
      `/naur/${organizationId}/${propertyId}/applicable-fraction-initialization`,
      options,
    );
  }

  /**
   * Cheap, always-200 check of whether this property's one-time applicable fraction
   * initialization has already run. A single indexed read — safe to call on page load.
   * A property that was never initialized returns alreadyInitialized: false, not a 404.
   */
  getApplicableFractionInitializationState(
    organizationId: string,
    propertyId: string,
    options?: Object,
  ): Promise<ApplicableFractionInitializationStatus> {
    if (!organizationId || !propertyId) {
      throw new Error(
        'Organization ID and Property ID are required to check applicable fraction initialization state',
      );
    }

    return get(
      `/naur/${organizationId}/${propertyId}/applicable-fraction-initialization/state`,
      options,
    );
  }

  /**
   * Runs the property's first applicable fraction calculation. One-time and irreversible.
   *
   * `fullError` is required, not incidental: it gives the caller the parsed error body
   * instead of a string, so a blocked run can be distinguished by the presence of
   * `findings` in that body rather than by HTTP status.
   */
  initializeApplicableFractions(
    organizationId: string,
    propertyId: string,
    options?: Object,
  ): Promise<ApplicableFractionInitializationSuccess> {
    if (!organizationId || !propertyId) {
      throw new Error(
        'Organization ID and Property ID are required to initialize applicable fractions',
      );
    }

    return post(
      `/naur/${organizationId}/${propertyId}/applicable-fraction-initialization`,
      undefined,
      { ...options, fullError: true },
    );
  }
}
